import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { ROLES } from "../../lib/roleOptions";
import TerritoryPicker from "../../components/common/TerritoryPicker";
import { FIELD_ROLES } from "../../lib/population";
import { Send, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function InviteCreateDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales_officer");
  const [territories, setTerritories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setEmail("");
    setRole("sales_officer");
    setTerritories([]);
    setError("");
    setResult(null);
    setCopied(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setError("");
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/invites", { email: email.trim().toLowerCase(), role,
        territory_unit_ids: territories });
      setResult(data);
      toast.success("Invite created");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!result?.dev_invite_link) return;
    try {
      await navigator.clipboard.writeText(result.dev_invite_link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Could not copy — select the link manually.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="invite-create-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle>Invite a new user</DialogTitle>
              <DialogDescription>
                Send a single-use invite link. The recipient sets their own password on accept.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-callisto-navy">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@callistosfa.com"
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid="invite-email-input"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-callisto-navy">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-callisto-navy">Territory units (optional)</Label>
                <TerritoryPicker
                  fieldRole={FIELD_ROLES.includes(role)}
                  value={territories}
                  onChange={setTerritories}
                  testIdPrefix="invite-territories"
                />
              </div>
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="invite-error">
                  {error}
                </div>
              )}
              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-3 py-2">
                Email delivery is currently mocked. After creating, copy the invite link from the next screen and share it manually.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
                data-testid="invite-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send size={14} className="mr-1.5" /> Create invite</>)}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={20} /> Invite created
              </DialogTitle>
              <DialogDescription>
                Share this single-use link with{" "}
                <span className="font-semibold text-callisto-navy">{result.email}</span>. It expires in 7 days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-callisto bg-callisto-app p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">Invite link</p>
                <p
                  className="mt-1.5 text-xs text-callisto-navy break-all font-mono select-all"
                  data-testid="invite-link-text"
                >
                  {result.dev_invite_link}
                </p>
              </div>
              <Button
                onClick={copyLink}
                variant="outline"
                className="w-full h-10 border-callisto-navy text-callisto-navy hover:bg-callisto-navy hover:text-callisto-gold"
                data-testid="invite-copy-link"
              >
                {copied ? <><CheckCircle2 size={14} className="mr-1.5" /> Copied</> : <><Copy size={14} className="mr-1.5" /> Copy link</>}
              </Button>
            </div>
            <DialogFooter>
              <Button
                onClick={handleClose}
                className="bg-callisto-navy hover:bg-callisto-navy/90 text-white w-full"
                data-testid="invite-done"
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
